import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import styles from 'stylesheets/components/star_rating.module';

import { BrowserAbilities } from 'javascripts/shared/utils/browser_abilities';
import classNames from 'classnames';
import numbro from 'numbro';
import pluralize from 'pluralize';
import _ from 'lodash';

const STAR_COUNT = 5;
const STAR_WIDTHS = {
  xsmall: 16,
  small: 20,
  medium: 24,
  large: 28,
  xlarge: 34,
};
const SIZE_STYLES = {
  xsmall: styles.xsmall,
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
  xlarge: styles.xlarge,
};

const hoverEnabled = BrowserAbilities.hoverEnabled();

export function StarRating(props) {
  const [rating, setRating] = useState(props.rating);
  const [currentRating, setCurrentRating] = useState(null);
  const textSize = props.textSize || props.size;

  const containerWidth = useMemo(() => {
    return STAR_WIDTHS[props.size] * STAR_COUNT;
  }, [props.size]);

  const activeStarsWidth = useMemo(() => {
    return (rating / STAR_COUNT) * containerWidth;
  }, [containerWidth, rating]);

  useEffect(() => {
    setRating(currentRating || props.rating);
  }, [currentRating, props.rating]);

  function handleStarMouseEnter(hoverRating) {
    if (!hoverEnabled) return null;
    return setCurrentRating(hoverRating);
  }

  function handleStarClick(clickedRating) {
    return props.onRate(clickedRating);
  }

  function handleContainerMouseLeave() {
    if (!props.interactive || !hoverEnabled) return null;
    return setCurrentRating(null);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      return handleStarClick(e);
    }
    return true;
  }

  // Render Helpers //
  function renderStarSvg(size, activeClass, interactiveClass) {
    const outlineClass = props.showEmptyOutlines && currentRating === null ? styles.outlines : '';
    return (
      <div
        data-testid='star-rating-star'
        className={classNames(styles.star, size, activeClass, interactiveClass, outlineClass)}
      >
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 192 192'>
          <path d='M171.73,81.35c-.64,1.77-2.08,3-3.39,4.27l-4.95,4.83-9.48,9.25-9.63,9.39-4.68,4.56c-.78.76-1.6,1.49-2.34,2.28-.92,1-.47,1.89-.25,3.14q2.21,12.8,4.41,25.61,1.15,6.65,2.28,13.29c.49,2.87,2.2,8.25-1.2,10.07a5.1,5.1,0,0,1-4.34-.19c-2.06-.87-4-2.06-6-3.1l-12-6.31-11.53-6.06-5.76-3L100,147.83a15.54,15.54,0,0,0-3.36-1.77c-.9-.18-1.84.6-2.62,1l-2.88,1.51-6.24,3.28-12,6.31-12,6.32c-1.76.93-3.53,1.87-5.3,2.78-1.58.82-3.36,1.56-5.15.92-3.64-1.31-2.26-6.2-1.79-9,.74-4.39,1.51-8.78,2.26-13.17q2.28-13.26,4.56-26.52c.13-.75.68-2.18.45-2.91s-1.41-1.46-1.92-2L49.18,110l-9.69-9.43-9.6-9.34-4.52-4.39c-1.41-1.37-3.1-2.71-4.06-4.46-2.22-4,1.59-6.08,5.07-6.61,4.25-.64,8.5-1.25,12.75-1.86l26.15-3.8,3.09-.45a9.32,9.32,0,0,0,2.85-.44c.65-.38,1-1.67,1.3-2.31l1.66-3.37,5.95-12C84,43.58,87.82,35.59,91.87,27.75c.75-1.45,1.8-3,3.45-3.51,1.88-.57,3.56.66,4.6,2.13a39.73,39.73,0,0,1,2.81,5.46l3.13,6.32L117.67,62l2.9,5.87c.47,1,.5,1.27,1.55,1.45,1.22.21,2.46.35,3.68.53l13.27,1.93c8.84,1.28,17.67,2.55,26.51,3.86C168.5,76.09,173.16,77.4,171.73,81.35Z' />
        </svg>
      </div>
    );
  }

  function renderStar(idx, active) {
    const size = SIZE_STYLES[props.size];
    const activeClass = active ? styles.active : styles.inactive;
    const interactiveClass = props.interactive ? styles.interactive : styles.readOnly;
    const starNumber = idx + 1;
    const screenReaderText = active ? null : (
      <span className={styles.screenReaderOnly}>{`${starNumber} ${pluralize('star', starNumber)}`}</span>
    );

    if (!props.interactive) {
      return (
        <div
          className={classNames(styles.starRateTrigger, interactiveClass)}
          data-rating={starNumber}
          key={idx}
          data-interactive={interactiveClass}
        >
          {renderStarSvg(size, activeClass, interactiveClass)}
        </div>
      );
    }

    return (
      <button
        className={classNames(styles.starRateTrigger, interactiveClass)}
        data-rating={starNumber}
        aria-pressed={active ? null : starNumber === rating}
        tabIndex={active ? '-1' : '0'}
        onMouseEnter={() => handleStarMouseEnter(starNumber)}
        onClick={() => handleStarClick(starNumber)}
        onKeyPress={handleKeyPress}
        key={idx}
        data-interactive={interactiveClass}
      >
        {screenReaderText}
        {renderStarSvg(size, activeClass, interactiveClass)}
      </button>
    );
  }

  function renderStars(active) {
    return _.times(STAR_COUNT, idx => renderStar(idx, active));
  }

  function renderRating() {
    if (!props.showRating) {
      return null;
    }
    return (
      <span className={styles.rating} aria-hidden='true'>
        {rating.toFixed(1)}
      </span>
    );
  }

  function renderRatingsCount() {
    if (!props.showRatingsCount || _.isNil(props.ratingsCount)) {
      return null;
    }

    const numberFormatParams =
      props.ratingsCount < 1000
        ? {}
        : {
            average: true,
            totalLength: 2,
          };
    const count = numbro(props.ratingsCount).format(numberFormatParams);

    let ratingsCountText;
    if (props.showRatingsCountFullText) {
      // Full rating count ie: (10 ratings)
      ratingsCountText = `(${count} ${pluralize('rating', props.ratingsCount)})`;
    } else {
      // Short rating count  ie: (10)
      ratingsCountText = `(${count})`;
    }

    if (props.ratingsCountLink) {
      return (
        <a href={props.ratingsCountLink} className={styles.ratingsCount}>
          {ratingsCountText}
        </a>
      );
    }
    return <span className={styles.ratingsCount}>{ratingsCountText}</span>;
  }

  return (
    <div
      className={classNames(styles.starRating, {
        [styles.verticalDisplay]: props.verticalDisplay,
        [styles.singleLineDisplay]: props.singleLineDisplay,
      })}
      tabIndex='0'
      data-testid='star-rating'
    >
      {!props.interactive && (
        <span className={styles.screenReaderOnly}>{`${rating.toFixed(1)} ${pluralize('star', rating)}`}</span>
      )}
      <div className={styles.stars} onMouseLeave={() => handleContainerMouseLeave()} style={{ width: containerWidth }}>
        {renderStars(false)}
        <div className={styles.activeStars} data-active-rating={rating} style={{ width: activeStarsWidth }}>
          {renderStars(true)}
        </div>
      </div>
      <div className={classNames(styles.ratingWrapper, SIZE_STYLES[textSize])}>
        {renderRating()}
        {renderRatingsCount()}
      </div>
    </div>
  );
}

StarRating.propTypes = {
  interactive: PropTypes.bool,
  onRate: PropTypes.func,
  rating: PropTypes.number,
  ratingsCount: PropTypes.number,
  ratingsCountLink: PropTypes.string,
  showRating: PropTypes.bool,
  showRatingsCount: PropTypes.bool,
  showRatingsCountFullText: PropTypes.bool,
  showEmptyOutlines: PropTypes.bool,
  singleLineDisplay: PropTypes.bool,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge']),
  textSize: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge']),
  verticalDisplay: PropTypes.bool,
};

StarRating.defaultProps = {
  interactive: false,
  onRate: _.noop,
  rating: 0,
  showRating: false,
  showRatingsCount: false,
  showRatingsCountFullText: true,
  showEmptyOutlines: false,
  singleLineDisplay: false,
  size: 'medium',
  verticalDisplay: false,
};
